<template>
  <div v-if="activeSession" class="session">
    <AttensiDropdown
      v-model="activeSession"
      name="dropdown-1"
      empty-card
      default-card-spacing
      :options="selectableSessions"
      @onChange="setSession"
    >
      <template #input-content>
        <OptimizedImage
          class="company-logo"
          :src="activeSession.logo_url"
          alt=""
          sizes="128px"
          role="presentation"
        />
      </template>
      <template #dropdown-content>
        <label
          v-if="hasMorethanOneSession"
          class="company-search-holder"
          aria-label="search company"
        >
          <AttensiInput
            id="company-search"
            v-model="searchQuery"
            name="company-search"
            type="text"
            :placeholder="$t('sessions.SEARCH_SESSION')"
            autofocus
            @change.native="searchSession"
          />
        </label>

        <div class="session-button-container">
          <BaseTooltip
            position="bottom"
            class="session-button-container__tooltip"
          >
            <template #content>
              {{ $t('sessions.HELPER') }}
            </template>
            <template #trigger>
              <BaseButton
                size="medium"
                outline
                theme="blue"
                tabindex="-1"
                class="session-button-container__button"
                @click="addSession()"
              >
                {{ $t('sessions.ADD_SESSION') }}
              </BaseButton>
            </template>
          </BaseTooltip>
        </div>

        <ul v-if="hasMorethanOneSession" class="session-list">
          <li
            v-for="(session, index) in sessionList"
            :key="index"
            class="session-item"
            @click="setSession(session)"
          >
            <button
              :aria-label="session.name"
              :title="session.name"
              class="session-item__button"
            >
              <OptimizedImage
                :src="session.logo_url"
                class="company-logo"
                sizes="128px"
                alt=""
                role="presentation"
              />
            </button>
          </li>
          <span v-if="isEmptySearch">
            {{ $t('sessions.NO_SESSIONS') }}
          </span>
        </ul>
      </template>
    </AttensiDropdown>
  </div>
</template>

<script>
import OptimizedImage from '@/components/generics/OptimizedImage/OptimizedImage.vue'
import { useProductFilters } from '@/hooks/useProductFilters'
import { pickCompany } from '@/service/loginActions'
import { AttensiDropdown, AttensiInput } from '@attensi/materia'
import { getActiveSession, getSessions } from '@attensi/session'
import { useUserFilter } from '../user/hooks/useUserFilter'
import BaseButton from './base/BaseButton.vue'
import BaseTooltip from './base/BaseTooltip.vue'

export default {
  components: {
    BaseButton,
    AttensiDropdown,
    AttensiInput,
    BaseTooltip,
    OptimizedImage,
  },
  setup() {
    const { deleteLocalStorageProductFilters } = useProductFilters()
    const { resetFilterParams: resetUserFilters } = useUserFilter()
    return {
      deleteLocalStorageProductFilters,
      resetUserFilters,
    }
  },
  data() {
    return {
      activeSession: null,
      sessions: null,
      searchQuery: '',
    }
  },
  computed: {
    selectableSessions() {
      return (
        this.sessions?.filter(
          (session) => session.subdomain !== this.activeSession?.subdomain
        ) ?? []
      )
    },
    sessionList() {
      return this.selectableSessions.filter((session) =>
        session.name.toLowerCase().includes(this.searchQuery.toLowerCase())
      )
    },
    isSessionListEmpty() {
      return this.sessionList.length === 0
    },
    isSearching() {
      return this.searchQuery.length > 0
    },
    isEmptySearch() {
      return this.isSearching && this.isSessionListEmpty
    },
    hasMorethanOneSession() {
      return this.sessions?.length > 1
    },
  },
  async created() {
    this.sessions = await getSessions()
    this.activeSession = await getActiveSession()
  },
  methods: {
    setSession(session) {
      this.deleteLocalStorageProductFilters()
      this.resetUserFilters()
      window.location.href = `${location.origin}/${session.subdomain}`
    },
    addSession() {
      this.deleteLocalStorageProductFilters()
      this.resetUserFilters()
      pickCompany()
    },
  },
}
</script>

<style lang="scss" scoped>
.session {
  position: relative;
  text-align: center;
  width: 100%;

  .session-list {
    display: flex;
    flex-direction: column;
    gap: $spacing-3xs;
  }

  .session-item {
    &__button {
      background: none;
      border: none;
      width: 100%;
      border-radius: $spacing-3xs;
      padding: $spacing-3xs;
      cursor: pointer;

      &:hover {
        background: $color-pebble;
      }
    }
  }
  .company-logo {
    object-fit: contain;
    height: 45px;
    max-width: 50%;

    @media screen and (max-width: $tablet-width-max) {
      height: 30px;
    }
  }

  .session-button-container {
    width: 100%;
    margin: $spacing-3xs 0;

    &__tooltip,
    &__button {
      width: 100%;
    }
  }
}
</style>
